import { z } from 'zod';
import {
  getVisualStyleLabel,
  visualStyleLabelIdSchema,
  visualStyleVocabulary,
  type VisualStyleLabel,
  type VisualStyleLabelId,
} from '../style-labels/vocabulary';
import {
  buildDeterministicStyleNarrative,
  parseStyleNarrative,
  validateStyleNarrative,
} from './narrative';
import { styleProfileSchema, type StyleProfile } from './schema';

export const countInstructionWords = (value: string) =>
  value.trim().split(/\s+/).filter(Boolean).length;

export const styleRefinementInstructionSchema = z
  .string()
  .trim()
  .min(2, 'Tell STYLE what you want to change.')
  .max(400, 'Keep the instruction to 50 words or fewer.')
  .refine((value) => countInstructionWords(value) <= 50, {
    message: 'Keep the instruction to 50 words or fewer.',
  });

export const styleRefinementDraftSchema = z
  .object({
    coreLabelIds: z.array(visualStyleLabelIdSchema).length(3),
    supportingLabelIds: z.array(visualStyleLabelIdSchema).max(3),
    styleNarrative: z.string().min(1).max(1200),
    uncertainty: z.array(z.string().min(3).max(240)).max(3),
  })
  .superRefine((draft, context) => {
    const selected = [...draft.coreLabelIds, ...draft.supportingLabelIds];
    if (new Set(selected).size !== selected.length) {
      context.addIssue({
        code: 'custom',
        path: ['supportingLabelIds'],
        message: 'Core and supporting labels must be distinct.',
      });
    }
  });

export type StyleRefinementDraft = z.infer<typeof styleRefinementDraftSchema>;

export interface StyleRefinementProvenance {
  provider: string;
  model: string;
  usedFallback?: boolean;
}

export const applyStyleRefinement = (
  current: StyleProfile,
  instruction: string,
  draft: StyleRefinementDraft,
  provenance: StyleRefinementProvenance,
): StyleProfile => {
  const cleanInstruction = styleRefinementInstructionSchema.parse(instruction);
  const parsedDraft = styleRefinementDraftSchema.parse(draft);
  const core = parsedDraft.coreLabelIds as [
    VisualStyleLabelId,
    VisualStyleLabelId,
    VisualStyleLabelId,
  ];
  const supporting = parsedDraft.supportingLabelIds;
  const selected = [...core, ...supporting];
  const candidates = [...current.candidateLabels];

  for (const labelId of selected) {
    if (!candidates.some((item) => item.labelId === labelId)) {
      const label = getVisualStyleLabel(labelId)!;
      candidates.push({
        labelId,
        coverage: 0,
        averageConfidence: 0,
        genericness: label.genericness,
        distinctiveness: label.distinctiveness,
        redundancyPenalty: 0,
        score: 0,
        sourceImageIds: [current.imageAnalyses[0]?.imageId ?? 'written-refinement'],
        evidence: ['Added in response to your written Style Card refinement.'],
      });
    }
  }
  const selectedSet = new Set(selected);
  const boundedCandidates = [
    ...candidates.filter((item) => selectedSet.has(item.labelId)),
    ...candidates.filter((item) => !selectedSet.has(item.labelId)),
  ].slice(0, 30);

  const suppliedNarrative = validateStyleNarrative(parsedDraft.styleNarrative, core, supporting);
  const styleNarrative = suppliedNarrative.valid
    ? parsedDraft.styleNarrative
    : buildDeterministicStyleNarrative(core, supporting);

  return styleProfileSchema.parse({
    ...current,
    candidateLabels: boundedCandidates,
    calibratedCoreLabelIds: core,
    calibratedSupportingLabelIds: supporting,
    styleNarrative,
    narrativeLabelTokens: parseStyleNarrative(styleNarrative, selected).tokens,
    uncertainty: [
      ...current.uncertainty.slice(0, 2),
      ...parsedDraft.uncertainty,
      ...(provenance.usedFallback || !suppliedNarrative.valid
        ? ['A validated local rendering was used for part of this written refinement.']
        : []),
    ].slice(0, 5),
    userAdjustments: {
      edited: true,
      previousCoreLabelIds:
        current.userAdjustments.previousCoreLabelIds ?? current.calibratedCoreLabelIds,
      updatedAt: new Date().toISOString(),
      instruction: cleanInstruction,
      refinementProvider: provenance.provider,
      refinementModel: provenance.model,
      refinementUsedFallback: provenance.usedFallback || !suppliedNarrative.valid,
    },
  });
};

const stopWords = new Set([
  'and',
  'change',
  'feel',
  'from',
  'have',
  'into',
  'just',
  'like',
  'look',
  'make',
  'more',
  'much',
  'please',
  'style',
  'than',
  'that',
  'this',
  'very',
  'want',
  'with',
]);

const normaliseWord = (word: string) => {
  const clean = word.toLocaleLowerCase().replace(/[^a-z0-9]/g, '');
  if (clean.endsWith('ier') && clean.length > 5) return `${clean.slice(0, -3)}y`;
  if (clean.endsWith('er') && clean.length > 5) return clean.slice(0, -2);
  return clean;
};

const phraseTerms = (label: VisualStyleLabel) => [
  label.label.toLocaleLowerCase(),
  label.id.replaceAll('_', ' '),
  ...label.aliases.map((alias) => alias.toLocaleLowerCase()),
];

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const isNegated = (instruction: string, terms: string[]) =>
  terms.some((term) =>
    new RegExp(
      `\\b(?:less|not|avoid|remove|without|lose)\\s+(?:[a-z]+\\s+){0,2}${escapeRegExp(term)}\\b`,
      'i',
    ).test(instruction),
  );

const instructionWords = (instruction: string) => {
  const words = instruction.toLocaleLowerCase().split(/\s+/);
  const positive = new Set<string>();
  let negationBudget = 0;
  for (const rawWord of words) {
    const word = normaliseWord(rawWord);
    if (['less', 'not', 'avoid', 'remove', 'without', 'lose'].includes(word)) {
      negationBudget = 3;
      continue;
    }
    if (!word) continue;
    if (negationBudget > 0) {
      negationBudget -= 1;
      continue;
    }
    if (word.length >= 4 && !stopWords.has(word)) positive.add(word);
  }
  return positive;
};

const labelScores = (instruction: string) => {
  const query = instruction.toLocaleLowerCase();
  const words = instructionWords(instruction);
  return visualStyleVocabulary.labels
    .map((label) => {
      const terms = phraseTerms(label);
      const negated = isNegated(query, terms);
      const exact =
        !negated &&
        terms.some((term) => new RegExp(`\\b${escapeRegExp(term)}\\b`, 'i').test(query));
      const searchable = [
        label.label,
        label.id.replaceAll('_', ' '),
        label.shortDescription,
        ...label.aliases,
      ]
        .join(' ')
        .toLocaleLowerCase();
      const overlap = negated ? 0 : [...words].filter((word) => searchable.includes(word)).length;
      return {
        label,
        negated,
        direct: exact,
        score: (exact ? 20 : 0) + overlap * 2 + label.distinctiveness / 10,
      };
    })
    .sort((left, right) => right.score - left.score);
};

export const buildLocalStyleRefinement = (
  current: StyleProfile,
  instruction: string,
): StyleRefinementDraft => {
  const cleanInstruction = styleRefinementInstructionSchema.parse(instruction);
  const scores = labelScores(cleanInstruction);
  const excluded = new Set(scores.filter((item) => item.negated).map((item) => item.label.id));
  const ranked = scores
    .filter((item) => !item.negated && item.score >= 2)
    .map((item) => item.label.id);
  const direct = scores.filter((item) => item.direct && !item.negated).map((item) => item.label.id);
  const related = direct.flatMap((id) => getVisualStyleLabel(id)?.relatedLabelIds ?? []);
  const existing = [...current.calibratedCoreLabelIds, ...current.calibratedSupportingLabelIds];
  const fallback = visualStyleVocabulary.labels
    .filter((label) => !excluded.has(label.id))
    .sort(
      (left, right) =>
        right.distinctiveness - left.distinctiveness || left.genericness - right.genericness,
    )
    .map((label) => label.id);
  const pool = [...direct, ...related, ...ranked, ...existing, ...fallback];
  const selected: VisualStyleLabelId[] = [];
  for (const labelId of pool) {
    if (!excluded.has(labelId) && !selected.includes(labelId)) selected.push(labelId);
    if (selected.length === 6) break;
  }
  const core = selected.slice(0, 3) as [VisualStyleLabelId, VisualStyleLabelId, VisualStyleLabelId];
  const supporting = selected.slice(3, 6);
  return styleRefinementDraftSchema.parse({
    coreLabelIds: core,
    supportingLabelIds: supporting,
    styleNarrative: buildDeterministicStyleNarrative(core, supporting),
    uncertainty: [
      'This written refinement updates the editorial reading without re-analysing the images.',
    ],
  });
};

export const refineStyleProfileLocally = (
  current: StyleProfile,
  instruction: string,
  provenance: StyleRefinementProvenance = {
    provider: 'mock',
    model: 'deterministic-written-refinement-v1',
    usedFallback: true,
  },
) =>
  applyStyleRefinement(
    current,
    instruction,
    buildLocalStyleRefinement(current, instruction),
    provenance,
  );
